import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import AuthField from "../Auth/AuthField";
import EyeIcon from "../Auth/EyeIcon";
import MasterModal from "./MasterModal";

export default function MasterProfileModal({ open, onClose, userId }) {
	const { user: loggedInUser } = useContext(AuthContext);
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [saving, setSaving] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open || !userId) return;
		setLoading(true);
		api
			.get(`/users/${userId}`)
			.then(({ data }) => {
				setName(data.name || "");
				setEmail(data.email || "");
				setPassword("");
			})
			.catch(toastError)
			.finally(() => setLoading(false));
	}, [open, userId]);

	const handleClose = () => {
		setPassword("");
		setShowPassword(false);
		onClose();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!userId) return;
		setSaving(true);
		const payload = { name: name.trim(), email: email.trim() };
		if (password.trim()) payload.password = password;
		try {
			await api.put(`/users/${userId}`, payload);
			toast.success(i18n.t("userModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const footer = (
		<>
			<button type="button" className="btn-secondary" onClick={handleClose} disabled={saving}>
				{i18n.t("userModal.buttons.cancel")}
			</button>
			<button
				type="submit"
				form="master-profile-form"
				className="btn-primary"
				disabled={saving || loading}
			>
				{saving ? i18n.t("master.pages.saving") : i18n.t("userModal.buttons.okEdit")}
			</button>
		</>
	);

	return (
		<MasterModal
			open={open}
			onClose={handleClose}
			title={i18n.t("userModal.title.edit")}
			subtitle={loggedInUser?.email}
			footer={footer}
			maxWidth={480}
		>
			{loading ? (
				<div className="master-loading">
					<div className="master-spinner" />
					<span>{i18n.t("master.pages.loading")}</span>
				</div>
			) : (
				<form id="master-profile-form" onSubmit={handleSubmit} className="space-y-1">
					<AuthField id="master-name" label={i18n.t("userModal.form.name")}>
						<input
							id="master-name"
							type="text"
							className="form-input"
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							autoFocus
						/>
					</AuthField>
					<AuthField id="master-email" label={i18n.t("userModal.form.email")}>
						<input
							id="master-email"
							type="email"
							className="form-input"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
						/>
					</AuthField>
					<AuthField
						id="master-password"
						label={i18n.t("userModal.form.password")}
						hint={i18n.t("master.pages.passwordOptional")}
					>
						<div className="relative">
							<input
								id="master-password"
								type={showPassword ? "text" : "password"}
								className="form-input !pr-10"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								autoComplete="new-password"
							/>
							<button
								type="button"
								className="absolute right-0 top-0 flex h-[2.3125rem] items-center px-3 text-default-500 hover:text-default-800"
								onClick={() => setShowPassword((v) => !v)}
								tabIndex={-1}
							>
								<EyeIcon open={showPassword} />
							</button>
						</div>
					</AuthField>
				</form>
			)}
		</MasterModal>
	);
}
